// eslint-disable-next-line aloft/no-sui-chakra
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@aloft/toolkit-sui/chakra';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

const CLOSE_DELAY_MS = 150;

export type HoverPopoverHelpers = {
  /** Suppresses the next scheduled close for `durationMs` (default 300ms). Useful when
   * content resizes/shrinks in place (e.g. a "show more/less" toggle) and the cursor
   * briefly ends up outside the now-smaller content box — without this, that would
   * incorrectly close the popover. */
  suppressCloseBriefly: (durationMs?: number) => void;
};

type Props = {
  children: ReactNode;
  renderContent: ReactNode | ((helpers: HoverPopoverHelpers) => ReactNode);
  placement?: 'right-start' | 'top' | 'bottom' | 'left';
  contentWidth?: string;
};

/**
 * A popover that opens on hover and stays open for a short delay after the mouse leaves,
 * so briefly crossing the gap between trigger and content doesn't dismiss it
 */
export const HoverPopover = ({
  children,
  renderContent,
  placement = 'right-start',
  contentWidth = '240px',
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressClose = useRef(false);

  const cancelClose = () => {
    if (!closeTimer.current) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const scheduleClose = () => {
    if (suppressClose.current) return;
    cancelClose();
    closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  };

  const suppressCloseBriefly = (durationMs = 300) => {
    suppressClose.current = true;
    setTimeout(() => {
      suppressClose.current = false;
    }, durationMs);
  };

  return (
    <Popover isOpen={isOpen} placement={placement} isLazy>
      <PopoverTrigger>
        <span
          style={{ display: 'inline-flex', alignItems: 'center' }}
          onMouseEnter={() => {
            cancelClose();
            setIsOpen(true);
          }}
          onMouseLeave={scheduleClose}
        >
          {children}
        </span>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          sx={{
            width: contentWidth,
            borderRadius: '4px',
            border: 'none',
            boxShadow: '0px 8px 16px rgba(0,0,0,0.16), 0px 0px 8px rgba(0,0,0,0.08)',
            padding: 0,
            _focus: {
              outline: 'none',
              boxShadow: '0px 8px 16px rgba(0,0,0,0.16), 0px 0px 8px rgba(0,0,0,0.08)',
            },
          }}
        >
          <PopoverArrow />
          {typeof renderContent === 'function'
            ? renderContent({ suppressCloseBriefly })
            : renderContent}
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
